import { useMutation, useQueryClient } from '@tanstack/react-query'
import api, { IOrdinal } from 'api/api'
import cn from 'classnames'
import React, { useEffect, useMemo, useState } from 'react'
import { UserPlus } from 'lucide-react'

type Props = {
    ordinalId?: string | number | null
    initialValue?: boolean
    className?: string
    activeClassName?: string
    animateClassName?: string
    showLabel?: boolean
    size?: number
}

export default function ShowInProfileButton({
    ordinalId,
    initialValue,
    className,
    activeClassName,
    animateClassName,
    showLabel = true,
    size = 16,
}: Props) {
    const [active, setActive] = useState(!!initialValue)
    const [animate, setAnimate] = useState(false)
    const queryClient = useQueryClient()

    useEffect(() => {
        setActive(!!initialValue)
    }, [initialValue])

    const id = useMemo(() => {
        const raw = ordinalId === null || ordinalId === undefined ? '' : String(ordinalId).trim()
        return raw || null
    }, [ordinalId])

    const mutation = useMutation({
        mutationFn: async () => {
            if (!id) throw new Error('Missing ordinal id')
            return await api.toggleOrdinalShowInProfile(id)
        },
        onMutate: async () => {
            const prev = active
            setActive(!prev)
            return { prev }
        },
        onError: (_err, _vars, ctx) => {
            setActive(!!ctx?.prev)
        },
        onSuccess: res => {
            const next = !!res?.users_ordinal?.show_in_profile
            setActive(next)

            const userId = gon.currentUser?.id
            if (!userId) return

            queryClient.setQueryData(
                ['treechatOrdinals', userId],
                (old: IOrdinal[] | undefined) => {
                    if (!Array.isArray(old)) return old
                    return old.map((o: IOrdinal) => {
                        if (String(o?.id || '') !== String(res?.id || '')) return o
                        return { ...o, users_ordinal: res.users_ordinal }
                    })
                },
            )

            // Ensure any other server-derived fields stay consistent (and keep media stream details
            // view in sync, which relies on this query for `users_ordinal`).
            queryClient.invalidateQueries({ queryKey: ['treechatOrdinals', userId] })

            // Refresh any mounted user-ordinals streams that depend on show_in_profile filters.
            // (The full key is ['ordinalsMediaStream', serializeFilterKey(...)], so invalidate only
            // those whose serialized filter includes `"user_ordinals"` to avoid refetching the
            // regular ordinals stream.)
            queryClient.invalidateQueries({
                predicate: q => {
                    const key = q.queryKey
                    if (!Array.isArray(key)) return false
                    if (key[0] !== 'ordinalsMediaStream') return false
                    return typeof key[1] === 'string' && key[1].includes('"user_ordinals"')
                },
            })
        },
    })

    const disabled = !id || mutation.isPending

    return (
        <button
            type="button"
            className={cn(className, active && activeClassName, animate && animateClassName)}
            onClick={e => {
                e.stopPropagation()
                e.preventDefault()
                if (disabled) return
                setAnimate(true)
                window.setTimeout(() => setAnimate(false), 350)
                mutation.mutate()
            }}
            disabled={disabled}
            aria-pressed={active}
            title={active ? 'Shown in Profile' : 'Show in Profile'}
        >
            <UserPlus size={size} />
            {showLabel && <span>{active ? 'Shown in Profile' : 'Show in Profile'}</span>}
        </button>
    )
}
